/* eslint-disable react-hooks/exhaustive-deps */
import { faCirclePlus, faInfoCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";

import { Salary172Perquisites } from "../../../../config/filingConstants";
import { IAddIncomeForm } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import PriceDisplay from "../../../PriceDisplay";
import { ContextAwareToggle } from "../../../accordion";
import { NumericInputField, SelectInputField } from "../../../fields";

const SalarySection17TwoForm = () => {
	const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IAddIncomeForm>();

	const dispatch = useAppDispatch();
	const [disableInput, setDisableInput] = useState(false);

	return (
		<>
			<Accordion className="accordion-sm" defaultActiveKey="gross-salary-1-b">
				<Accordion.Item eventKey="gross-salary-1-b">
					<ContextAwareToggle eventKey="gross-salary-1-b" isChild={true}>
						<div className="btn-content justify-content-between">
							<Form.Label className="leading-para mb-0">
								I(b). Perquisites u/s 17(2)
								<span
									className="info-wrap"
									role="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										dispatch(
											showHintModal({
												title: "Perquisites under Section 17(2)",
												componentName: "Perquisites172Hint"
											})
										);
									}}
								>
									<FASvgIcon icon={faInfoCircle} />
								</span>
							</Form.Label>
							<div
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
								}}
								className="flex-grow-1"
							>
								<NumericInputField
									showLeftIcon={true}
									name="PerquisitesUnderSection17_2"
									value={values?.PerquisitesUnderSection17_2 || ""}
									onChange={(value) => setFieldValue("PerquisitesUnderSection17_2", value)}
									onBlur={handleBlur("PerquisitesUnderSection17_2")}
									isInvalid={
										Boolean(errors.PerquisitesUnderSection17_2) &&
										touched.PerquisitesUnderSection17_2
									}
									isDisabled={disableInput}
								/>
							</div>
						</div>
					</ContextAwareToggle>
					<Accordion.Body className="py-0">
						<Section17TwoBreakupForm handleOnSumChange={(val) => setDisableInput(() => val)} />
					</Accordion.Body>
				</Accordion.Item>
			</Accordion>
		</>
	);
};

export default SalarySection17TwoForm;

type FormProps = { handleOnSumChange: (isDisabled: boolean) => void };

const Section17TwoBreakupForm: React.FC<FormProps> = (props: FormProps) => {
	const { handleOnSumChange } = props;

	const { values, setFieldValue } = useFormikContext<IAddIncomeForm>();

	const [sum, setSum] = useState(0);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());

	useEffect(() => {
		let _sum = 0;
		values?.PerquisitesDescriptions?.forEach((item) => {
			if (item.length && item[1]) {
				_sum += Number(item[1]) || 0;
				setIsFirstRender(() => false);
			}
		});

		setSum(() => _sum);

		// also update the gross total in upper section
		handleOnSumChange(_sum > 0);

		if (isFirstRender && !_sum) {
			setFieldValue("PerquisitesUnderSection17_2", values?.PerquisitesUnderSection17_2 || "");
		} else if (!isFirstRender) {
			setFieldValue("PerquisitesUnderSection17_2", _sum);
		} else if (isFirstRender && _sum) {
			setFieldValue("PerquisitesUnderSection17_2", _sum);
		}
	}, [currentTime]);

	return (
		<>
			<Card className="more-info-card shadow-none">
				<Card.Header>
					<Card.Title>Break up of perquisites under section 17(2)</Card.Title>
				</Card.Header>
				<Card.Body>
					<section>
						<FieldArray name="PerquisitesDescriptions">
							{({ push, replace, remove, form }) => (
								<>
									{values?.PerquisitesDescriptions && values?.PerquisitesDescriptions?.length > 0
										? values?.PerquisitesDescriptions.map((_, index) => (
												<section className="mb-3 mb-sm-0" key={`salary-allowance-${index}`}>
													<Salary172Form
														index={index}
														form={form}
														onRemove={(itemIdx: number) => {
															if (values.PerquisitesDescriptions.length === 1) {
																replace(itemIdx, ["", ""]);
															} else {
																remove(itemIdx);
															}
															setCurrentTime(new Date().getTime());
														}}
														handleAmountInc={() => setCurrentTime(new Date().getTime())}
													/>
												</section>
										  ))
										: null}
									<PreventEditComponent>
										<Button variant="link" className="btn-add-link" onClick={() => push(["", ""])}>
											<FASvgIcon icon={faCirclePlus} />
											Add more
										</Button>
									</PreventEditComponent>
								</>
							)}
						</FieldArray>
					</section>
				</Card.Body>
				<Card.Footer>
					<p className="total-content">
						<span className="total-label">Total:</span>
						<span className="total-amount">
							<PriceDisplay price={sum} />
						</span>
					</p>
				</Card.Footer>
			</Card>
		</>
	);
};

type Props = {
	index: number;
	form: FormikProps<IAddIncomeForm>;
	onRemove: (itemIdx: number) => void;
	handleAmountInc: () => void;
};

const Salary172Form: React.FC<Props> = (props: Props) => {
	const { index, onRemove, form, handleAmountInc } = props;

	const { setFieldValue, handleBlur, values, touched, errors } = form;

	return (
		<>
			<Row className="align-items-center">
				<Col sm={6}>
					<SelectInputField
						placeholder="Select other"
						label="Description"
						name={`PerquisitesDescriptions.${index}[0]`}
						isOptionArrayOfObject={false}
						options={Salary172Perquisites}
						value={[values.PerquisitesDescriptions[index][0]]}
						onChange={(selectedOption) => {
							if (selectedOption) {
								setFieldValue(`PerquisitesDescriptions.${index}[0]`, selectedOption.value);
							} else {
								setFieldValue(`PerquisitesDescriptions.${index}[0]`, undefined);
							}
						}}
						onBlur={handleBlur(`PerquisitesDescriptions.${index}[0]`)}
						isInvalid={
							Boolean(getIn(errors, `PerquisitesDescriptions.${index}[0]`)) &&
							getIn(touched, `PerquisitesDescriptions.${index}[0]`)
						}
						errorMsg={getIn(errors, `PerquisitesDescriptions.${index}[0]`)}
					/>
				</Col>
				<Col sm>
					<NumericInputField
						showLeftIcon={true}
						placeholder=""
						label="Amount"
						name={`PerquisitesDescriptions.${index}[1]`}
						value={values.PerquisitesDescriptions[index][1]}
						onBlur={handleBlur(`PerquisitesDescriptions.${index}[1]`)}
						onChange={(value) => {
							setFieldValue(`PerquisitesDescriptions.${index}[1]`, value);
							handleAmountInc();
						}}
						isInvalid={
							Boolean(getIn(errors, `PerquisitesDescriptions.${index}[1]`)) &&
							getIn(touched, `PerquisitesDescriptions.${index}[1]`)
						}
						errorMsg={getIn(errors, `PerquisitesDescriptions.${index}[1]`)}
					/>
				</Col>
				<PreventEditComponent>
					<Col sm="auto" className="ps-0">
						<Button
							variant="transparent"
							className="text-greyDark icon-only d-none d-sm-flex"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
						</Button>
						<Button
							variant="transparent"
							className="btn-delete-full d-sm-none"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
							<span>Delete</span>
						</Button>
					</Col>
				</PreventEditComponent>
			</Row>
			{index !== values.PerquisitesDescriptions.length - 1 && (
				<hr className="d-block d-sm-none my-4" />
			)}
		</>
	);
};
